/**
 * Connect — per-client timeline view (OMON-209 + OMON-212).
 *
 * Resolves an EoC's sensor lanes (Firestore registry via observation.device[]),
 * loads the most recent MONTH of downsampled telemetry from BigQuery via the
 * `coordinator_sensors` callable (OMON-290 — then lazily backfills the rest of
 * the last year behind it), applies the user's saved sensor filter (OMON-212),
 * and mounts the per-day Konva swimlane. A Filter control lets the user choose
 * which sensors to show; the choice persists per-user (profile.ts).
 */
import { timeFormat } from "d3-time-format";
import { auth } from "../firebase";
import { loadClaims } from "../admin/claims";
import { escapeHtml } from "../utils/html";
import { loadSensorLanes } from "./sensors";
import {
  loadSensorSeries,
  loadSensorSeriesWindow,
  fortnightWindow,
  intervalForSpanMs,
  DEFAULT_WINDOW_DAYS,
  type DeviceHealth,
} from "./sensor-series";
import {
  planArchiveChunks,
  ARCHIVE_TOTAL_DAYS,
  ARCHIVE_CHUNK_DAYS,
} from "./archive-plan";
import { shouldReanchorLive } from "./live-tail";
import { mountDispositionButton } from "./disposition";
import { loadErrorMessage } from "./load-error";
import { openFlagsEditor } from "./flags-editor";
import { go } from "../router";
import {
  TimelineRenderer,
  type RenderLane,
  type RenderPoint,
} from "./swimlane/timeline-renderer";
import { openDevicePanel, orderDevices, type PanelDevice } from "./device-panel";
import { sweetTreeMarkSvg } from "../sweettree_loader";
import {
  selectedSensorKeys,
  swimlaneDevicePrefs,
  saveSelectedSensors,
  type SensorFilterEntry,
} from "./profile";

const fmtStamp = timeFormat("%a %d %b %H:%M");

// Live update (single-store, no Firestore live-tail — see
// reference_omon_coordinator_single_store): re-poll a short recent slice of
// BigQuery and merge. 30s feels live for minute-rate sensor dots; the 90-min
// lookback cheaply re-covers the trailing 30-min buckets so partial buckets
// settle and nothing is missed at the seam.
const LIVE_POLL_MS = 30_000;
const LIVE_LOOKBACK_MS = 90 * 60_000;
// OMON-343: streaming is a DEMO affordance, and a visible wall display defeats
// the document.hidden gate — a client sat on this view streaming for four days
// (2026-09-09..13) at ~£90/day of BigQuery. Stream hard-stops this long after
// each toggle-on, no matter what; the button restarts it.
const STREAM_MAX_MS = 15 * 60_000;
// OMON-224: when zoomed in we fetch only the VISIBLE window (at a pixel-density
// interval), padded by this fraction of the span each side so small pans stay
// instant — a re-fetch fires only when you pan/zoom past the loaded margin.
const WINDOW_PAD = 0.5;

let activeRenderer: TimelineRenderer | null = null;
let fullLanes: RenderLane[] = [];
let fullHealth: Record<string, DeviceHealth> = {};
let viewWindow: [Date, Date] = [new Date(), new Date()];
let pollTimer: number | null = null;
let visibilityHandler: (() => void) | null = null;
// OMON-222: bucket resolution fullLanes is currently loaded at (adapts to zoom).
let loadedInterval = 30;
// OMON-225: user-selectable look-back length (days), reset per view.
let windowDays = DEFAULT_WINDOW_DAYS;
// OMON-227: is the view "live-tailing" (parked at/near the right edge) vs the
// user having deliberately scrubbed into the past? Drives whether an unattended
// view catches up to now on return (laptop sleep / hours backgrounded). Reset
// per view; flipped by user zoom/pan via the renderer's onViewChange.
let liveTail = true;
// OMON-290: the ARCHIVE layer — the rest of the last year, lazily backfilled
// behind the initial month at 60-min resolution (the coarsest interval, and
// exactly what pixel density wants at year-wide zoom). Kept SEPARATE from
// fullLanes on purpose: every detail path (window change, re-anchor, refocus
// refresh, zoom refetch) REPLACES fullLanes wholesale, and history the user
// already has must survive all of them. Composition happens in shownLanes():
// archive points strictly OLDER than the detail window are prepended, so
// detail always wins inside its own span. The renderer is view-bounded
// (builtRange = view ± span, rasterised), so a year of points in memory is
// data-array weight only, not draw weight.
const archivePoints = new Map<string, RenderPoint[]>(); // filterKey → oldest-first
let archiveStartMs: number | null = null; // oldest covered instant
let archiveTopMs: number | null = null; // newest covered instant
let archiveFilling = false;
const ARCHIVE_INTERVAL_MIN = 60;
// OMON-290 lifecycle token. ☠️ `pollTimer === null` used to double as the
// torn-down sentinel, which (a) killed the backfill on arrival when invoked
// before the poll was wired, and (b) stopped meaning anything once streaming
// became optional (pollTimer is legitimately null with the view alive). Every
// async completion now checks the epoch instead.
let viewEpoch = 0;
// OMON-290 F1: memoised archive⊕detail composition, keyed per lane. compose()
// used to build a fresh concat array on EVERY shownLanes() call — which not
// only churned allocation, it broke the renderer's WeakMap colour caches
// (keyed by points-array IDENTITY), so gradient/state palettes were recomputed
// over the full year on every 30s poll and filter toggle. Entries invalidate
// by reference: a new detail lane, a grown archive buffer, or a moved detail
// window each produce a fresh composition; otherwise the SAME array is
// returned and the renderer's caches hit.
interface ComposedEntry {
  lane: RenderLane;
  buf: RenderPoint[] | undefined;
  lo: number;
  hi: number;
  out: RenderLane;
}
const composedCache = new Map<string, ComposedEntry>();

function teardown(): void {
  viewEpoch++;
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (visibilityHandler) {
    document.removeEventListener("visibilitychange", visibilityHandler);
    visibilityHandler = null;
  }
  activeRenderer?.destroy();
  activeRenderer = null;
  // OMON-290: the archive is per-client history — never carry it across views.
  archivePoints.clear();
  archiveStartMs = null;
  archiveTopMs = null;
  archiveFilling = false;
  composedCache.clear();
}

/** OMON-290: archive points OUTSIDE the detail window [loMs, hiMs] for one
 *  lane — a prefix strictly older and a suffix strictly newer. Both sides
 *  matter: an adaptive-zoom fetch RELOCATES the detail window into the past,
 *  and without the suffix the road back to "now" rendered blank (Ed's
 *  drag-home stall, 2026-08-12). Points are oldest-first; two binary-ish
 *  scans from the ends stay cheap. */
function archiveOutside(
  filterKey: string,
  loMs: number,
  hiMs: number,
): { prefix: RenderPoint[]; suffix: RenderPoint[] } {
  const buf = archivePoints.get(filterKey);
  if (!buf || buf.length === 0) return { prefix: [], suffix: [] };
  let i = buf.length;
  while (i > 0 && buf[i - 1].t.getTime() >= loMs) i--;
  let j = 0;
  while (j < buf.length && buf[j].t.getTime() <= hiMs) j++;
  return {
    prefix: i === 0 ? [] : buf.slice(0, i),
    suffix: j >= buf.length ? [] : buf.slice(j),
  };
}

/** OMON-290: the renderer's data range — the detail window, widened to
 *  wherever the archive reaches on BOTH sides. Left: zooming out past the
 *  loaded month lands on real (60-min) history, not a wall. Right: a detail
 *  window relocated into the past must not drag the axis's "now" edge back
 *  with it. */
function renderRange(): [Date, Date] {
  const startMs =
    archiveStartMs !== null
      ? Math.min(archiveStartMs, viewWindow[0].getTime())
      : viewWindow[0].getTime();
  const endMs =
    archiveTopMs !== null
      ? Math.max(archiveTopMs, viewWindow[1].getTime())
      : viewWindow[1].getTime();
  return [new Date(startMs), new Date(endMs)];
}

/** Split a `${propertyPath}::${subtype}` key back into a savable entry. */
function entryFromKey(key: string): SensorFilterEntry {
  const i = key.indexOf("::");
  return { propertyPath: key.slice(0, i), subtype: key.slice(i + 2) };
}

// OMON-234: the EoC whose timeline is currently open. The sensor filter is saved
// and read PER-CLIENT, so shownLanes/currentSelection/persist key off this.
let currentEocId = "";

/** Lanes to show for this client. Walks devices in their original order and, for
 *  each device, includes its selected channels OR a single BLANK placeholder row
 *  when all of its channels are filtered out — so a device whose sensors are all
 *  hidden keeps a visible, clickable gutter rather than vanishing entirely
 *  (OMON-234: a device must never disappear, leaving no way to bring it back).
 *  An empty saved filter, or one that matches none of this client's lanes (e.g.
 *  stale after a device swap), shows everything. */
/** Apply the user's per-client device order + visibility (MONO-162): group
 *  the lane list by device, drop hidden devices, emit in saved order (listed
 *  first, unlisted appended — new hardware always shows). Runs LAST in
 *  shownLanes so it composes with the sensor filter and archive layering. */
function applyDevicePrefs(lanes: RenderLane[]): RenderLane[] {
  const prefs = swimlaneDevicePrefs(currentEocId);
  if (prefs.order.length === 0 && prefs.hidden.length === 0) return lanes;
  const hidden = new Set(prefs.hidden);
  const groups: { deviceId: string; lanes: RenderLane[] }[] = [];
  const idx = new Map<string, number>();
  for (const l of lanes) {
    let i = idx.get(l.deviceId);
    if (i === undefined) {
      i = groups.length;
      idx.set(l.deviceId, i);
      groups.push({ deviceId: l.deviceId, lanes: [] });
    }
    groups[i].lanes.push(l);
  }
  return orderDevices(
    groups.filter((g) => !hidden.has(g.deviceId)),
    prefs.order,
  ).flatMap((g) => g.lanes);
}

function shownLanes(): RenderLane[] {
  // OMON-290: compose detail + archive here, the one funnel every render path
  // goes through — filter toggles, live merges, zoom refetches and re-anchors
  // all inherit the archive without knowing it exists. Detail wins inside its
  // own window; the archive supplies everything OUTSIDE it, on both sides.
  const loMs = viewWindow[0].getTime();
  const hiMs = viewWindow[1].getTime();
  const compose = (l: RenderLane): RenderLane => {
    const buf = archivePoints.get(l.filterKey);
    const c = composedCache.get(l.filterKey);
    if (c && c.lane === l && c.buf === buf && c.lo === loMs && c.hi === hiMs) {
      return c.out; // stable identity → the renderer's colour caches hit
    }
    const { prefix, suffix } = archiveOutside(l.filterKey, loMs, hiMs);
    const out =
      prefix.length === 0 && suffix.length === 0
        ? l
        : { ...l, points: prefix.concat(l.points, suffix) };
    composedCache.set(l.filterKey, { lane: l, buf, lo: loMs, hi: hiMs, out });
    return out;
  };
  const keys = selectedSensorKeys(currentEocId);
  if (keys.size === 0) return applyDevicePrefs(fullLanes.map(compose));
  const order: string[] = [];
  const byDevice = new Map<string, RenderLane[]>();
  for (const l of fullLanes) {
    if (!byDevice.has(l.deviceId)) {
      byDevice.set(l.deviceId, []);
      order.push(l.deviceId);
    }
    byDevice.get(l.deviceId)!.push(l);
  }
  const out: RenderLane[] = [];
  let anyShown = false;
  for (const did of order) {
    const deviceLanes = byDevice.get(did)!;
    const shown = deviceLanes.filter((l) => keys.has(l.filterKey));
    if (shown.length > 0) {
      out.push(...shown.map(compose));
      anyShown = true;
    } else {
      // Blank placeholder: same gutter (device name/photo/health), no data, a
      // filterKey that can't collide with a real channel. Clicking its gutter
      // reopens the per-device filter (which reads fullLanes) to re-enable.
      out.push({ ...deviceLanes[0], points: [], filterKey: `${did}::__blank__`, subLabel: "" });
    }
  }
  // No device had any selected lane (filter matches nothing for this client) →
  // treat as no filter and show all, rather than an all-blank screen.
  return applyDevicePrefs(anyShown ? out : fullLanes.map(compose));
}

export async function renderConnectTimeline(
  eocDocId: string,
  clientName: string,
): Promise<void> {
  const panel = document.getElementById("panel-connect");
  if (!panel) return;
  teardown();
  currentEocId = eocDocId;
  // OMON-290 (Ed): the swimlane earns the whole pane — the 1200px panel cap
  // suits the overview cards, not a 17-lane timeline on a wide monitor. The
  // class comes off on every route back to the overview (renderConnectOverview
  // and renderConnectLoading both clear it).
  panel.classList.add("connect-wide");
  // OMON-290: this mount's lifecycle token. teardown() bumps the epoch, so
  // every async completion below compares against the value captured here.
  const epoch = viewEpoch;
  const alive = () => viewEpoch === epoch;
  // Devices-panel bridge: the button wires before applyFilter exists. It
  // must point at applyFilter (the zoom-preserving updateData path), NOT
  // applyAndRender — a full render() refits the view to the whole loaded
  // range, so a reorder yanked a 3-day window out to the entire archive
  // (Ed's striped-bands screenshot, 2026-08-28). Same OMON-245 lesson as
  // the sensor filter: a prefs change alters WHICH lanes show, never WHERE
  // the user is looking.
  let applyPrefsRef: (() => void) | null = null;

  panel.innerHTML = `
    <div class="connect-timeline">
      <header class="connect-header connect-timeline-header">
        <button class="connect-back" type="button">← Clients</button>
        <h2>${escapeHtml(clientName)}</h2>
        <div class="connect-filter-wrap">
          <label class="connect-window-wrap">Window
            <!-- Ed 2026-08-26: day-scale reading is the real use — the old
                 7/14/30/60 ladder skewed archival. Deeper history still
                 arrives via the archive backfill behind the window. -->
            <select id="connectWindowSel" class="connect-window-sel">
              <option value="1">1 day</option>
              <option value="3" selected>3 days</option>
              <option value="7">7 days</option>
              <option value="14">14 days</option>
            </select>
          </label>
          <button class="connect-filter-btn" type="button" id="connectAlertsBtn" hidden>Alerts</button>
          <button class="connect-filter-btn" type="button" id="connectFlagsBtn">Flags</button>
          <button class="connect-filter-btn" type="button" id="connectDevicesBtn">Devices</button>
          <!-- OMON-290 (Ed, 2026-08-12): live streaming is OPT-IN. The 30s
               poll is a BigQuery job per tick per open tab; a forgotten demo
               laptop was spending real money. Cloud-Logging-style affordances:
               one-shot Refresh, and a Stream toggle that is OFF on every
               mount, deliberately never persisted. -->
          <button class="connect-icon-btn" type="button" id="connectRefreshBtn"
                  data-tip="Refresh" aria-label="Refresh">
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M20 12a8 8 0 1 1-2.34-5.66M20 4v4h-4"/></svg>
          </button>
          <button class="connect-icon-btn" type="button" id="connectStreamBtn"
                  data-tip="Stream data — auto-refresh every 30s"
                  aria-label="Stream data" aria-pressed="false">
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true"><g fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5.5 8.5a6 6 0 0 0 0 7M18.5 8.5a6 6 0 0 1 0 7M8.3 10.3a3 3 0 0 0 0 3.4M15.7 10.3a3 3 0 0 1 0 3.4"/></g><circle cx="12" cy="12" r="1.6" fill="currentColor"/></svg>
          </button>
        </div>
        <p class="connect-sub" id="connectTimelineStatus">Loading sensors…</p>
      </header>
      <div id="connectCanvas" class="connect-canvas"></div>
      <div class="connect-filter-panel" id="connectFilterPanel" hidden></div>
    </div>`;

  panel.querySelector(".connect-back")?.addEventListener("click", () => {
    // OMON-253: return to the overview via the hash router so the URL resets to
    // #/connect (keeps URL and view consistent; refresh-safe from the list too).
    teardown();
    go({ kind: "panel", panel: "connect" });
  });

  // Per-client push-notification flags editor (replaces the Firefoo list view).
  // MONO-162: the Devices panel (reorder + show/hide). The badge makes the
  // hidden state discoverable from the main view — nothing vanishes silently.
  const devicesBtn = panel.querySelector<HTMLButtonElement>("#connectDevicesBtn");
  const updateDevicesBadge = () => {
    if (!devicesBtn) return;
    const n = swimlaneDevicePrefs(currentEocId).hidden.length;
    devicesBtn.textContent = n > 0 ? `Devices · ${n} hidden` : "Devices";
  };
  updateDevicesBadge();
  devicesBtn?.addEventListener("click", () => {
    const seen = new Set<string>();
    const devices: PanelDevice[] = [];
    for (const l of fullLanes) {
      if (seen.has(l.deviceId)) continue;
      seen.add(l.deviceId);
      devices.push({
        deviceId: l.deviceId,
        name: l.deviceName,
        room: l.topLabel,
        image: l.deviceImage,
      });
    }
    openDevicePanel(currentEocId, devices, devicesBtn, () => {
      updateDevicesBadge();
      applyPrefsRef?.();
    });
  });

  panel.querySelector("#connectFlagsBtn")?.addEventListener("click", () => {
    void openFlagsEditor(eocDocId, clientName);
  });

  // OMON-375: who is alerted for this client (responder disposition).
  const alertsBtn = panel.querySelector<HTMLButtonElement>("#connectAlertsBtn");
  if (alertsBtn) mountDispositionButton(alertsBtn, eocDocId, clientName);

  const status = panel.querySelector<HTMLElement>("#connectTimelineStatus");
  const canvas = panel.querySelector<HTMLElement>("#connectCanvas");
  const filterPanel = panel.querySelector<HTMLDivElement>("#connectFilterPanel");
  if (!canvas) return;
  const setStatus = (s: string) => {
    if (status) status.textContent = s;
  };

  // Loading-affordance (Ed, 2026-08-12): the status TEXT is the weakest place to say "loading" —
  // the user's eyes are on the canvas they just pinched, not the header. So
  // every USER-INITIATED fetch spins the Refresh control, because a pinch
  // that refetches is the same operation as clicking Refresh and should wear
  // the same badge. It also disambiguates the thing that actually misleads
  // here: in a sensor swimlane a GAP is real data (a dead sensor, a quiet
  // night), so "still loading" must never look like "nothing to show".
  //
  // Depth-counted, not a boolean: a zoom refetch can land while another fetch
  // is in flight, and the first completion must not clear a spinner the second
  // one still needs. try/finally guarantees it clears on error too — a stuck
  // spinner (permanent "loading") would be worse than none.
  //
  // Deliberately NOT wired to the 30s stream poll or the refocus catch-up:
  // those are automatic, and a badge that blinks by itself stops answering
  // "did MY action do something?". Streaming already says "live" in red.
  let loadDepth = 0;
  const setLoading = (on: boolean) => {
    loadDepth = Math.max(0, loadDepth + (on ? 1 : -1));
    panel
      .querySelector<HTMLButtonElement>("#connectRefreshBtn")
      ?.classList.toggle("spinning", loadDepth > 0);
  };
  const withLoading = async <T>(work: Promise<T>): Promise<T> => {
    setLoading(true);
    try {
      return await work;
    } finally {
      setLoading(false);
    }
  };

  // OMON-290 (Ed): the tree, not an empty rectangle, while the first data
  // loads. Konva doesn't mount until data arrives, so the canvas slot is free;
  // the SAME mark and animation as the app's boot loader (index.html.template
  // owns the .stc-leaf keyframes; the artwork lives in sweettree_loader.ts),
  // removed at the first real render. Initial mount only — window changes and
  // re-anchors keep old content on screen.
  canvas.innerHTML = `
    <div class="connect-boot" role="status" aria-label="Loading sensor timeline">
      ${sweetTreeMarkSvg("mark")}
    </div>`;

  // OMON-215: the sensor filter is now per-device — clicking a device's gutter
  // opens a panel of just that device's channels (the per-device replacement for
  // the old global "Filter sensors" button).
  const openDeviceFilter = (deviceId: string, x: number, y: number) => {
    if (!filterPanel) return;
    // OMON-234: anchor the per-device filter popup to the clicked device's row
    // (canvas-relative coords) instead of floating at the top of the view,
    // disconnected from the device it controls.
    const canvasEl = panel.querySelector<HTMLElement>("#connectCanvas");
    filterPanel.style.top = `${(canvasEl?.offsetTop ?? 0) + y}px`;
    filterPanel.style.left = `${(canvasEl?.offsetLeft ?? 0) + x}px`;
    filterPanel.hidden = false;
    buildDeviceFilterPanel(filterPanel, deviceId, applyFilter);
  };

  // OMON-222: assigned once data is loaded; re-fetches at a zoom-matched
  // resolution. Held in a mutable so applyAndRender (defined earlier) can wire it.
  let adaptiveZoom: ((start: Date, end: Date) => void) | null = null;
  // OMON-233: the zoom/pan refetch is QUEUED rather than dropped, so it survives a
  // collision with the 30s live poll (which shares the single `polling` fetch
  // slot). `pendingZoom` holds the latest desired window; `drainZoom` runs it the
  // moment the slot frees. Before this, a refetch that fired while a poll was in
  // flight was silently discarded — so a deep zoom sat on the coarse (often empty)
  // bands until the next 30s tick happened to redraw it.
  let drainZoom: (() => void) | null = null;
  let pendingZoom: [Date, Date] | null = null;

  const applyAndRender = () => {
    const shown = shownLanes();
    // First real render replaces the boot tree (harmless no-op afterwards).
    canvas.querySelector(".connect-boot")?.remove();
    // Re-render in place (don't teardown — that would kill the live poll).
    if (!activeRenderer) activeRenderer = new TimelineRenderer(canvas);
    activeRenderer.onDeviceFilter = openDeviceFilter;
    activeRenderer.onViewChange = (s, e) => adaptiveZoom?.(s, e);
    // Open on the detail window, not the whole range: once the archive has
    // loaded, renderRange() spans a year and "1 day" showed all of it.
    activeRenderer.render(shown, renderRange(), fullHealth, viewWindow);
    const end = viewWindow[1];
    setStatus(
      shown.length === 0
        ? `No sensors selected — click a device to choose (${fullLanes.length} available).`
        : `${shown.length} lane${shown.length === 1 ? "" : "s"} · ${windowDays}-day window to ${fmtStamp(end)} · click a device to filter`,
    );
  };

  // OMON-245: a filter toggle only changes WHICH lanes show — it doesn't need a
  // full Konva stage teardown+rebuild. Reuse the no-teardown updateData path (as
  // the live poll / zoom refetch already do), which also preserves the user's
  // zoom. Markedly cheaper than render() per toggle.
  const applyFilter = () => {
    if (!activeRenderer) {
      applyAndRender();
      return;
    }
    const shown = shownLanes();
    activeRenderer.updateData(shown, renderRange(), fullHealth);
    const end = viewWindow[1];
    setStatus(
      shown.length === 0
        ? `No sensors selected — click a device to choose (${fullLanes.length} available).`
        : `${shown.length} lane${shown.length === 1 ? "" : "s"} · ${windowDays}-day window to ${fmtStamp(end)} · click a device to filter`,
    );
  };
  applyPrefsRef = applyFilter;

  try {
    // Room labels: strip the hand-authored possessive prefix ("JM's
    // Bedroom" → "Bedroom"). The header already names the client, and in
    // pseudonymised environments the raw text leaked the REAL initials
    // next to a pseudonym (demo swimlane, 2026-09-08). Presentation only —
    // stored device data untouched; the estate-wide admin lists keep it.
    const lanes = (await loadSensorLanes(eocDocId)).map((l) => ({
      ...l,
      location: l.location.replace(/^[A-Za-z]{1,4}(?:\.[A-Za-z]{1,4})*'s\s+/, "") || l.location,
    }));
    if (lanes.length === 0) {
      canvas.querySelector(".connect-boot")?.remove();
      setStatus("No sensors registered for this client.");
      return;
    }

    // OMON-225: reset the look-back to the default for a fresh view, and pick the
    // overview interval from that span (pixel density).
    windowDays = DEFAULT_WINDOW_DAYS;
    liveTail = true; // OMON-227: a fresh view always starts pinned to now

    loadedInterval = intervalForSpanMs(windowDays * 86_400_000);
    setStatus(`Loading ${windowDays}-day telemetry from BigQuery…`);
    // OMON-343: this await had NO catch — a failed callable (the day the
    // BigQuery quota ran out) was an unhandled rejection and the panel sat
    // blank on "Loading…" forever. Fail into the status line instead.
    let initial;
    try {
      initial = await withLoading(loadSensorSeries(
        lanes,
        new Date(),
        loadedInterval,
        windowDays,
        // OMON-243: the _values-sourced lanes (push-sent dots, door opens) hydrate
        // AFTER first paint. Re-render when they land — but only if this view is
        // still current (guard against a window/EoC change during the async reads).
        (layered) => {
          if (fullLanes === layered) applyAndRender();
        },
      ));
    } catch (err) {
      console.error("[connect] initial telemetry load failed", err);
      if (!alive()) return;
      canvas.querySelector(".connect-boot")?.remove();
      setStatus(loadErrorMessage(err));
      return;
    }
    const { start, end, renderLanes, health } = initial;
    fullLanes = renderLanes;
    fullHealth = health;
    viewWindow = [start, end];

    if (fullLanes.length === 0) {
      setStatus(
        `${lanes.length} sensor channel${lanes.length === 1 ? "" : "s"} — none chartable in this build yet.`,
      );
      canvas.querySelector(".connect-boot")?.remove();
      return;
    }

    applyAndRender();

    // OMON-290: the month is painted — dig out the rest of the year behind it.
    // Newest-first chunks under the server's 60d cap, at the coarsest (60-min)
    // interval, each merged into the archive layer and rendered in place
    // (updateData preserves the user's zoom; the range just grows leftward).
    // Not generation-guarded on purpose: the archive is append-only history,
    // valid across window changes and re-anchors — only teardown stops it.
    const backfillArchive = async () => {
      if (archiveFilling || !alive()) return;
      archiveFilling = true;
      try {
        const floorMs = Date.now() - ARCHIVE_TOTAL_DAYS * 86_400_000;
        // Dig from NOW, not from the detail window's left edge: the archive is
        // the 60-min BASE LAYER for the whole year, including under the detail
        // month. When an adaptive-zoom fetch relocates the detail window into
        // the past, the archive suffix is what keeps the recent months on
        // screen — without the overlap, dragging home crossed a void (Ed,
        // 2026-08-12). Compose gives detail priority inside its own window,
        // so the overlap never double-draws.
        const fromMs = archiveStartMs ?? viewWindow[1].getTime();
        if (archiveTopMs === null) archiveTopMs = viewWindow[1].getTime();
        for (const { startMs, endMs } of planArchiveChunks(fromMs, floorMs)) {
          if (!alive()) return; // view torn down mid-dig
          const r = await loadSensorSeriesWindow(
            lanes,
            new Date(startMs),
            new Date(endMs),
            ARCHIVE_INTERVAL_MIN,
          );
          if (!alive()) return;
          let chunkHasPoints = false;
          for (const rl of r.renderLanes) {
            if (rl.points.length === 0) continue;
            chunkHasPoints = true;
            const buf = archivePoints.get(rl.filterKey);
            archivePoints.set(rl.filterKey, buf ? rl.points.concat(buf) : rl.points);
          }
          // OMON-290 (Ed, 2026-08-12): an entirely-empty chunk means the estate
          // predates it — the devices didn't exist yet. STOP digging (older
          // chunks are just more BigQuery spend on nothing) and clamp the axis
          // to the oldest REAL point, so zoom-out lands on the data's true left
          // edge instead of months of whitespace. Trade-off, accepted: a whole
          // 56-day gap with zero readings across EVERY device mid-history would
          // also stop the dig there.
          if (!chunkHasPoints) {
            let oldest = Number.POSITIVE_INFINITY;
            for (const buf of archivePoints.values()) {
              if (buf.length > 0) oldest = Math.min(oldest, buf[0].t.getTime());
            }
            archiveStartMs = Number.isFinite(oldest) ? oldest : null;
            break;
          }
          archiveStartMs = startMs;
          // Render in place — never through applyAndRender (that resets zoom).
          // OMON-290 F4: and never mid-gesture — a rebuild landing during a
          // drag fights the transform (2026-07-08 perf review D2, applied
          // here too). The next chunk or the final render below covers it.
          if (!activeRenderer?.isInteracting()) {
            activeRenderer?.updateData(shownLanes(), renderRange(), fullHealth);
          }
          // Breathe between chunks: keep BigQuery polite and the main thread
          // responsive while someone is actively scrubbing the fresh month.
          await new Promise((res) => setTimeout(res, 400));
        }
        // A final render so the empty-chunk clamp (which may have PULLED the
        // left edge in) takes effect immediately, not on the next interaction.
        activeRenderer?.updateData(shownLanes(), renderRange(), fullHealth);
        const months = Math.round(
          (viewWindow[1].getTime() - (archiveStartMs ?? fromMs)) / (30.44 * 86_400_000),
        );
        console.debug(`[connect] archive backfill complete: ~${months} months loaded`);
      } catch (err) {
        // Non-fatal by design: the month the user is looking at is unaffected.
        console.error("[connect] archive backfill failed (history stays partial)", err);
      } finally {
        archiveFilling = false;
      }
    };
    // Invoked near the bottom of this function. (Historical ☠️: the guard
    // used to be `pollTimer === null`, which read as "not started yet" here
    // and killed the dig on arrival; the epoch token replaced it — and had
    // to, once streaming made a null pollTimer a legitimate live state.)

    // OMON-225: window-length selector — reload the look-back at the chosen
    // length (CF cap is 60d) and reset zoom to the full window. Pixel-density
    // resolution means a 60-day overview just buckets coarser (~60-min), so the
    // payload stays bounded.
    const windowSel = panel.querySelector<HTMLSelectElement>("#connectWindowSel");
    windowSel?.addEventListener("change", async () => {
      const days = Number(windowSel.value) || DEFAULT_WINDOW_DAYS;
      if (days === windowDays) return;
      windowDays = days;
      loadedInterval = intervalForSpanMs(windowDays * 86_400_000);
      setStatus(`Loading ${windowDays}-day telemetry…`);
      const gen = ++fetchGen; // user intent: invalidate in-flight poll/zoom applies
      try {
        // Loading-affordance (Ed, 2026-08-12): user-initiated (they changed the look-back) → spin.
        const r = await withLoading(
          loadSensorSeries(lanes, new Date(), loadedInterval, windowDays),
        );
        if (gen !== fetchGen || !alive()) return; // superseded / torn down
        fullLanes = r.renderLanes;
        fullHealth = r.health;
        viewWindow = [r.start, r.end];
        liveTail = true; // OMON-227: window change reloads ending at now
        pendingZoom = null; // OMON-233: a queued zoom is stale once we reset the window
        applyAndRender(); // render() resets zoom to the full new window
        void backfillArchive(); // OMON-290: keep digging if the year isn't in yet
      } catch (err) {
        console.error("[connect] window change failed", err);
        setStatus(loadErrorMessage(err));
      }
    });

    // Live updates — incremental BigQuery re-poll, merged seamlessly (zoom/pan
    // preserved via renderer.updateData). Single store: no Firestore live-tail.
    let polling = false;
    // 2026-07-08 perf review D1: every fetch captures a generation at start and
    // drops its APPLY if a newer fetch began meanwhile. `polling` economises
    // (avoids duplicate in-flight CF calls); the generation is what makes stale
    // completions safe — a superseded zoom no longer paints old data first, and
    // a user action (window change) invalidates any in-flight poll's result.
    let fetchGen = 0;

    // OMON-227: re-anchor a live-tailing view to a fresh [now − windowDays, now]
    // overview, catching up across ANY gap (laptop sleep, hours/days of being
    // backgrounded). Only ever called when liveTail is true — the user is parked
    // at the live edge, not studying the past — so resetting zoom to the full
    // now-anchored window is the right thing after a long absence. This is what
    // makes "leave it 48h, come back to Sunday + Monday" actually true; the cheap
    // 30s trailing merges still handle the attended case.
    const reanchorLive = async () => {
      if (polling) return;
      polling = true;
      const gen = ++fetchGen;
      try {
        loadedInterval = intervalForSpanMs(windowDays * 86_400_000);
        const r = await loadSensorSeries(lanes, new Date(), loadedInterval, windowDays);
        if (gen !== fetchGen || !alive()) return; // superseded / torn down
        fullLanes = r.renderLanes;
        fullHealth = r.health;
        viewWindow = [r.start, r.end];
        liveTail = true;
        pendingZoom = null; // OMON-233: discard a stale zoom — resetting to the live overview
        applyAndRender(); // full render → zoom reset to the now-anchored window
      } catch (err) {
        console.error("[connect] live re-anchor failed", err);
        setStatus(loadErrorMessage(err));
      } finally {
        polling = false;
      }
    };

    const poll = async () => {
      if (polling) return; // don't overlap an in-flight poll
      // 2026-07-08 perf review D2: never land a merge+redraw mid-gesture — the
      // renderer knows when the user is dragging/zooming; the next 30s tick (or
      // the settle rebuild) covers whatever this tick would have fetched.
      if (activeRenderer?.isInteracting()) return;
      // OMON-224/227: a recent right edge gets the cheap trailing merge. A STALE
      // right edge means either (a) the user scrubbed into the past — leave it
      // alone — or (b) a live-tailing view that fell behind while the tab/laptop
      // slept — catch it up to now. (b) is the unattended-for-days case.
      const nowMs = Date.now();
      if (viewWindow[1].getTime() < nowMs - LIVE_LOOKBACK_MS) {
        if (shouldReanchorLive(viewWindow[1].getTime(), nowMs, liveTail, LIVE_LOOKBACK_MS)) {
          await reanchorLive();
        }
        return;
      }
      polling = true;
      const gen = ++fetchGen;
      const sinceMs = Date.now() - LIVE_LOOKBACK_MS;
      const now = new Date();
      try {
        const { renderLanes: fresh, health: freshHealth } = await loadSensorSeriesWindow(
          lanes,
          new Date(sinceMs),
          now,
          loadedInterval, // match the resolution fullLanes is loaded at
        );
        if (gen !== fetchGen || !alive()) return; // superseded / torn down
        const freshByKey = new Map(fresh.map((l) => [l.filterKey, l]));
        for (const lane of fullLanes) {
          const fl = freshByKey.get(lane.filterKey);
          // Replace only the trailing window with fresh data. If a lane has no
          // fresh points (a quiet device), KEEP its existing points — never
          // truncate already-loaded history to 90-min-ago.
          if (fl) {
            const kept = lane.points.filter((p) => p.t.getTime() < sinceMs);
            lane.points = kept.concat(fl.points);
          }
        }
        // Refresh the gutter health (battery/signal/last-seen) from the poll.
        fullHealth = { ...fullHealth, ...freshHealth };
        viewWindow = [viewWindow[0], now];
        const shown = shownLanes();
        activeRenderer?.updateData(shown, renderRange(), fullHealth);
        if (shown.length > 0) {
          setStatus(`${shown.length} lane${shown.length === 1 ? "" : "s"} · live · ${fmtStamp(now)}`);
        }
      } catch (err) {
        // A transient BQ hiccup must not kill the live loop — surface it so a
        // persistently-stale view is visible rather than silently old.
        console.error("[connect] live poll failed", err);
        setStatus(`live update failed · retrying · last ok ${fmtStamp(viewWindow[1])}`);
      } finally {
        polling = false;
        // OMON-233: a zoom/pan refetch that collided with this poll was queued —
        // run it now the slot is free, instead of making the user wait a tick.
        if (pendingZoom) drainZoom?.();
      }
    };
    // Tick on an interval, but ONLY while the tab is visible (don't burn BQ in
    // the background). Crucially, also poll IMMEDIATELY when the tab regains
    // focus — otherwise a tab-switching workflow shows up-to-30s-stale data.
    //
    // OMON-290 (Ed, 2026-08-12): streaming is OPT-IN, OFF on every mount, and
    // deliberately never persisted. Each poll tick is a BigQuery job via
    // omon-api — a forgotten open tab was spending real money around the
    // clock. The Stream toggle in the header owns this; the Refresh button is
    // the one-shot alternative.
    const startStreaming = () => {
      if (pollTimer !== null) return;
      const startedMs = Date.now();
      pollTimer = window.setInterval(() => {
        // OMON-343: absolute cap per toggle-on. Deliberately NOT idle-based —
        // the runaway client was a display nobody was touching but everyone
        // could see, so "interaction" is the wrong signal entirely.
        if (Date.now() - startedMs > STREAM_MAX_MS) {
          stopStreaming();
          setStreamUi(false);
          setStatus("Streaming stopped after 15 min — press Stream to restart");
          return;
        }
        if (!document.hidden) void poll();
      }, LIVE_POLL_MS);
      void poll(); // come current immediately, don't wait a tick
    };
    const stopStreaming = () => {
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
    };
    // On regaining focus after being backgrounded, a trailing 90-min poll could
    // leave a hole between the last-loaded point and the lookback window. So do
    // a FULL re-fetch on refocus (cheap on an occasional event) — the whole
    // window comes current with no gap. The 30s interval keeps the cheap
    // trailing merge for continuous updates while watching.
    const fullRefresh = async () => {
      if (polling) return;
      polling = true;
      const gen = ++fetchGen;
      try {
        // OMON-224: refresh the CURRENTLY-loaded window at its resolution (not
        // always the fortnight) — if it's live, advance the right edge to now to
        // close any gap that opened while backgrounded.
        const live = viewWindow[1].getTime() >= Date.now() - LIVE_LOOKBACK_MS;
        const end = live ? new Date() : viewWindow[1];
        const r = await loadSensorSeriesWindow(lanes, viewWindow[0], end, loadedInterval);
        if (gen !== fetchGen || !alive()) return; // superseded / torn down
        fullLanes = r.renderLanes;
        fullHealth = r.health;
        viewWindow = [r.start, r.end];
        const shown = shownLanes();
        activeRenderer?.updateData(shown, renderRange(), fullHealth);
        if (shown.length > 0) {
          const tail = live ? "live" : "detail";
          setStatus(`${shown.length} lane${shown.length === 1 ? "" : "s"} · ${tail} · ${fmtStamp(r.end)}`);
        }
      } catch (err) {
        console.error("[connect] full refresh failed", err);
        setStatus(loadErrorMessage(err));
      } finally {
        polling = false;
      }
    };
    // 2026-07-08 perf review D3: a tab-flipping workflow paid a full-window CF
    // refetch + Firestore reads on EVERY refocus. Debounce: skip if we refreshed
    // in the last 15s — the 30s poll keeps a watched tab current anyway. (The
    // long-gap re-anchor path stays unconditional; it exists for hours-long gaps.)
    let lastRefocusRefresh = 0;
    visibilityHandler = () => {
      if (document.hidden) return;
      // OMON-290: refocus catch-up is a STREAMING behaviour — a paused view
      // spends nothing until the user asks (Refresh) or turns streaming on.
      if (pollTimer === null) return;
      // OMON-227: a live-tailing view that fell badly behind while hidden (laptop
      // asleep, tab backgrounded for hours/days) re-anchors to now so the new days
      // appear; a fresh view, or one scrubbed into the past, takes the existing
      // in-place refresh (which preserves the user's zoom).
      if (shouldReanchorLive(viewWindow[1].getTime(), Date.now(), liveTail, LIVE_LOOKBACK_MS)) {
        void reanchorLive();
      } else {
        const now = Date.now();
        if (now - lastRefocusRefresh < 15_000) return;
        lastRefocusRefresh = now;
        void fullRefresh();
      }
    };
    document.addEventListener("visibilitychange", visibilityHandler);

    // OMON-290: the Cloud-Logging-style pair. Refresh = one shot, mirroring
    // the refocus logic (in-place if fresh, re-anchor if far behind). Stream =
    // the 30s poll, opt-in per mount.
    const refreshBtn = panel.querySelector<HTMLButtonElement>("#connectRefreshBtn");
    refreshBtn?.addEventListener("click", () => {
      // OMON-290 (Ed): visible proof the click did something — a few new
      // slivers at the right edge are easy to miss. Spin while the fetch is
      // in flight, with a THREE-rotation minimum (Ed's spec) so even an
      // instant response reads as an action, not a dead button.
      // Loading-affordance (Ed, 2026-08-12): goes through the shared depth counter now, so an overlapping
      // zoom/window fetch can't clear the spin out from under this click (and
      // vice versa). The three-rotation minimum stays CLICK-ONLY — it exists to
      // prove a button press landed; imposing 2.1s on every pinch would make
      // zooming feel sluggish, and a pinch already shows its own movement.
      const minSpin = new Promise((res) => setTimeout(res, 2100)); // 3 × 700ms
      const work = shouldReanchorLive(viewWindow[1].getTime(), Date.now(), liveTail, LIVE_LOOKBACK_MS)
        ? reanchorLive()
        : fullRefresh();
      void withLoading(Promise.allSettled([work, minSpin]));
    });
    const streamBtn = panel.querySelector<HTMLButtonElement>("#connectStreamBtn");
    const setStreamUi = (on: boolean) => {
      streamBtn?.classList.toggle("on", on);
      streamBtn?.setAttribute("aria-pressed", String(on));
      streamBtn?.setAttribute(
        "data-tip",
        on ? "Streaming — click to stop" : "Stream data — auto-refresh every 30s",
      );
    };
    // OMON-343: Stream is ADMIN-ONLY. It exists for demos; every other role
    // gets the one-shot Refresh. Fail-closed: the button stays hidden until
    // the admin claim is positively confirmed (null claims = no button).
    if (streamBtn) {
      streamBtn.hidden = true;
      const user = auth.currentUser;
      if (user) {
        void loadClaims(user).then((claims) => {
          if (claims?.role === "admin") streamBtn.hidden = false;
        });
      }
    }
    streamBtn?.addEventListener("click", () => {
      const on = pollTimer === null;
      setStreamUi(on);
      if (on) startStreaming();
      else stopStreaming();
    });

    // OMON-290: dig out the year. Bounded (~7 requests once, stopping early at
    // the estate's first data) — unlike streaming, this is a fixed cost that
    // buys the whole point of the view, so it stays automatic.
    void backfillArchive();

    // OMON-224: when the visible span settles after a zoom/pan, fetch the
    // VISIBLE window at a PIXEL-DENSITY resolution (intervalForSpanMs) — not the
    // whole fortnight. This is what lets deep zoom reach 1-min detail without a
    // 20k-bucket payload: buckets ≈ TARGET regardless of zoom because both the
    // window AND the interval scale with the span. We over-fetch by WINDOW_PAD
    // each side so small pans stay instant; a re-fetch fires only when the
    // (padded) visible window leaves the loaded one or the resolution changes.
    // Supersedes OMON-222's fortnight-at-interval (which floored detail at the
    // coarsest interval the fortnight could afford).
    // OMON-290: the pan/zoom fetch floor extends to wherever the archive
    // reaches — panning into last spring is a real request, not a clamp to the
    // detail window. The ceiling stays now.
    const horizon = () => {
      const h = fortnightWindow(new Date(), windowDays); // window floor / now ceil
      if (archiveStartMs !== null && archiveStartMs < h.start.getTime()) {
        h.start = new Date(archiveStartMs);
      }
      return h;
    };
    // The debounced view-change entry (renderer.onViewChange). It only RECORDS the
    // latest desired window + the live-tail intent, then asks drainZoom to run it.
    // Recording-not-fetching is what lets the refetch survive a colliding poll.
    adaptiveZoom = (vStart, vEnd) => {
      // OMON-227: record user intent. Parked within the live lookback of now →
      // still live-tailing (an unattended view should catch up on return);
      // scrubbed further into the past → leave it where they put it.
      liveTail = vEnd.getTime() >= Date.now() - LIVE_LOOKBACK_MS;
      pendingZoom = [vStart, vEnd]; // latest wins — coalesce rapid zoom/pan
      drainZoom?.();
    };
    // Runs the queued zoom refetch when the shared fetch slot is free. Re-entrant-
    // safe: if the slot is busy it no-ops and a finally (here or in the live poll)
    // re-drains; if a newer zoom arrives mid-fetch it runs straight after.
    drainZoom = () => {
      if (!alive()) { pendingZoom = null; return; } // view torn down
      if (polling || !pendingZoom) return; // slot busy → a finally will re-drain
      const [vStart, vEnd] = pendingZoom;
      pendingZoom = null;
      const spanMs = vEnd.getTime() - vStart.getTime();
      const want = intervalForSpanMs(spanMs);
      const pad = spanMs * WINDOW_PAD;
      const { start: floor, end: ceil } = horizon();
      let fStart = new Date(Math.max(vStart.getTime() - pad, floor.getTime()));
      const fEnd = new Date(Math.min(vEnd.getTime() + pad, ceil.getTime()));
      // OMON-290: the horizon can now be a YEAR deep, but a single request must
      // stay under the server's 60d cap. Clamp the fetch to the newest sub-cap
      // slice of the wanted window — anything older renders from the archive,
      // which at that zoom is the same 60-min resolution anyway.
      const capMs = ARCHIVE_CHUNK_DAYS * 86_400_000;
      if (fEnd.getTime() - fStart.getTime() > capMs) {
        fStart = new Date(fEnd.getTime() - capMs);
      }
      // Already covered at this resolution (panning within the loaded margin)? skip.
      if (
        want === loadedInterval &&
        fStart.getTime() >= viewWindow[0].getTime() &&
        fEnd.getTime() <= viewWindow[1].getTime()
      ) {
        return;
      }
      // OMON-290: the ARCHIVE also counts as coverage. At month-plus spans
      // `want` is the 60-min ceiling — exactly the archive's resolution — so a
      // fetch would return bytes we already hold. Before this check, EVERY
      // drag-settle in the archive region fired a 1–4s BigQuery fetch that
      // relocated the detail window and blocked the shared fetch slot: Ed's
      // stall-and-jump drag (2026-08-12). Skipping keeps dragging pure
      // transform + local rebuild.
      if (
        want >= ARCHIVE_INTERVAL_MIN &&
        archiveStartMs !== null &&
        archiveTopMs !== null &&
        fStart.getTime() >= archiveStartMs &&
        fEnd.getTime() <= Math.max(archiveTopMs, viewWindow[1].getTime())
      ) {
        return;
      }
      polling = true;
      const gen = ++fetchGen;
      // OMON-233: immediate feedback so a deep zoom reads as "fetching", not as a
      // frozen/blank lane during the ~350ms debounce + BigQuery round-trip.
      setStatus(`Loading ${want}-min detail…`);
      void (async () => {
        try {
          // Loading-affordance (Ed, 2026-08-12): the pinch/zoom refetch is the case Ed named — the lane
          // can look frozen or empty across the debounce + BigQuery round-trip.
          const r = await withLoading(loadSensorSeriesWindow(lanes, fStart, fEnd, want));
          if (gen !== fetchGen || !alive()) return; // superseded / torn down
          loadedInterval = want;
          fullLanes = r.renderLanes;
          fullHealth = r.health;
          viewWindow = [r.start, r.end];
          const shown = shownLanes();
          activeRenderer?.updateData(shown, renderRange(), fullHealth);
          if (shown.length > 0) {
            setStatus(`${shown.length} lane${shown.length === 1 ? "" : "s"} · ${want}-min detail · ${fmtStamp(r.end)}`);
          }
        } catch (err) {
          console.error("[connect] adaptive re-fetch failed", err);
        } finally {
          polling = false;
          if (pendingZoom) drainZoom?.(); // a newer zoom queued mid-fetch → run it
        }
      })();
    };
  } catch (err) {
    canvas.querySelector(".connect-boot")?.remove();
    setStatus("Failed to load sensor data.");
    console.error("[connect] timeline load failed", err);
  }
}

/** The currently-selected filter keys, expanded: an empty saved filter (OMON-212
 *  "no filter") means EVERY lane is shown, so expand it to the full set here so a
 *  per-device edit can toggle a subset without hiding everything else. */
function currentSelection(): Set<string> {
  const keys = selectedSensorKeys(currentEocId);
  return keys.size === 0 ? new Set(fullLanes.map((l) => l.filterKey)) : new Set(keys);
}

/**
 * OMON-215: per-device sensor filter. Clicking a device's gutter opens a panel
 * of just that device's channels. Toggling updates the GLOBAL OMON-212 allowlist
 * (other devices' selections untouched); when everything ends up checked we save
 * the empty "no filter" form so newly-added sensors keep auto-showing.
 */
function buildDeviceFilterPanel(
  panel: HTMLDivElement,
  deviceId: string,
  // The CHEAP updateData path (timeline-view's applyFilter) — not a stage rebuild.
  applyFilterFn: () => void,
): void {
  const deviceLanes = fullLanes.filter((l) => l.deviceId === deviceId);
  if (deviceLanes.length === 0) {
    panel.hidden = true;
    return;
  }
  const header = `${escapeHtml(deviceLanes[0].deviceName)} — ${escapeHtml(deviceLanes[0].topLabel)}`;

  const build = () => {
    const sel = currentSelection();
    panel.innerHTML = `
      <div class="connect-filter-head">
        <strong>${header}</strong>
        <button type="button" data-act="close" aria-label="Close">✕</button>
      </div>
      <div class="connect-filter-actions">
        <button type="button" data-act="all">Show all</button>
        <button type="button" data-act="none">Hide all</button>
      </div>
      ${deviceLanes
        .map(
          (l) => `
        <label class="connect-filter-row">
          <input type="checkbox" data-key="${escapeHtml(l.filterKey)}" ${sel.has(l.filterKey) ? "checked" : ""}>
          <span>${escapeHtml(l.subLabel)}</span>
        </label>`,
        )
        .join("")}`;

    // OMON-244: the checkbox flips instantly (native); the save + ONE re-render
    // (the cheap updateData path) run once the user pauses. The 200ms debounce
    // exists so a burst of ticks coalesces — see the flush-on-close below for
    // why the timer must never outlive the panel.
    let filterTimer: number | null = null;
    const flush = () => {
      filterTimer = null;
      // Start from the global selection, then apply THIS device's checkboxes.
      const selected = currentSelection();
      panel.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach((cb) => {
        const key = cb.dataset.key;
        if (!key) return;
        if (cb.checked) selected.add(key);
        else selected.delete(key);
      });
      // All lanes selected → save empty (no filter, new sensors auto-show).
      const entries =
        selected.size === fullLanes.length ? [] : [...selected].map(entryFromKey);
      // Fire-and-forget: the profile write must never sit on the redraw path.
      void saveSelectedSensors(currentEocId, entries);
      applyFilterFn();
    };
    const persist = () => {
      if (filterTimer !== null) clearTimeout(filterTimer);
      filterTimer = window.setTimeout(flush, 200);
    };

    panel.querySelectorAll<HTMLInputElement>("input[type=checkbox]").forEach((cb) =>
      cb.addEventListener("change", persist),
    );
    panel.querySelectorAll<HTMLButtonElement>("button[data-act]").forEach((b) =>
      b.addEventListener("click", () => {
        const act = b.dataset.act;
        if (act === "close") {
          // Flush a pending debounce NOW rather than letting the write+redraw
          // land 200ms AFTER the panel closed — that deferred work is what made
          // "select then close" feel frozen (2026-07-08 perf review, F1).
          if (filterTimer !== null) {
            clearTimeout(filterTimer);
            flush();
          }
          panel.hidden = true;
          return;
        }
        const check = act === "all";
        panel
          .querySelectorAll<HTMLInputElement>("input[type=checkbox]")
          .forEach((cb) => (cb.checked = check));
        persist();
      }),
    );
  };

  build();
  panel.hidden = false;
}
